import { useCallback, useEffect, useRef, useState } from 'react';

export type AudioTapStatus = 'idle' | 'loading' | 'playing' | 'paused';

/**
 * Lifts a live AnalyserNode out of an HTMLAudioElement for visualizers
 * (spectrum analyzer, frequency response, etc.) without breaking native
 * playback under Chrome's autoplay policy.
 *
 * Lifecycle (critical):
 *   1. handleAudioElement(el) — just stores the element. NO AudioContext is
 *      created here, because mounting a component is NOT a user gesture and
 *      Chrome will keep the context suspended (silent playback).
 *   2. resumeContext() — invoked SYNCHRONOUSLY from the play button click
 *      handler, inside the gesture call stack. On first call we lazily build
 *      the AudioContext + MediaElementSource + Analyser graph. On subsequent
 *      calls we just `ctx.resume()`.
 *
 * Why lazy: `createMediaElementSource` permanently redirects the element's
 * audio output through the graph. If the context is ever suspended after
 * that, audio plays silently into a dead-end. By deferring the tap until a
 * confirmed user gesture, we guarantee the graph is running before any
 * audio is routed through it.
 */
export function useAudioElementAnalyserTap(onAudioElement?: (el: HTMLAudioElement | null) => void) {
  const audioContextRef = useRef<AudioContext | null>(null);
  const mediaSourceRef = useRef<MediaElementAudioSourceNode | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const detachAudioListenersRef = useRef<(() => void) | null>(null);
  const elementRef = useRef<HTMLAudioElement | null>(null);
  // Tracks which element the graph is actually wired to. `createMediaElementSource`
  // can only be called once per element ever — track this to avoid the
  // dreaded `InvalidStateError: HTMLMediaElement already connected`.
  const tappedElementRef = useRef<HTMLAudioElement | null>(null);
  const [playerAnalyser, setPlayerAnalyser] = useState<AnalyserNode | null>(null);
  const [playerStatus, setPlayerStatus] = useState<AudioTapStatus>('idle');

  const detachListeners = useCallback(() => {
    detachAudioListenersRef.current?.();
    detachAudioListenersRef.current = null;
  }, []);

  const attachStatusListeners = useCallback((el: HTMLAudioElement) => {
    detachListeners();
    const syncStatus = () => {
      setPlayerStatus(
        el.paused
          ? (el.readyState < HTMLMediaElement.HAVE_CURRENT_DATA ? 'loading' : 'paused')
          : 'playing',
      );
    };
    const events = ['play', 'playing', 'pause', 'ended', 'waiting', 'loadstart', 'canplay', 'emptied'];
    events.forEach((ev) => el.addEventListener(ev, syncStatus));
    detachAudioListenersRef.current = () => {
      events.forEach((ev) => el.removeEventListener(ev, syncStatus));
    };
    syncStatus();
  }, [detachListeners]);

  const handleAudioElement = useCallback((el: HTMLAudioElement | null) => {
    // Same element identity → no-op (avoids tearing down a working graph
    // when React re-renders with a stable ref callback).
    if (el && elementRef.current === el) {
      try { onAudioElement?.(el); } catch {}
      return;
    }

    detachListeners();
    setPlayerAnalyser(null);
    setPlayerStatus('idle');

    // If our graph was wired to a DIFFERENT element, dispose source/analyser
    // (a MediaElementSource cannot be reused across elements).
    if (tappedElementRef.current && tappedElementRef.current !== el) {
      try { mediaSourceRef.current?.disconnect(); } catch {}
      try { analyserRef.current?.disconnect(); } catch {}
      mediaSourceRef.current = null;
      analyserRef.current = null;
      tappedElementRef.current = null;
    }

    elementRef.current = el;
    try { onAudioElement?.(el); } catch {}
    if (!el) return;

    // Listeners are cheap; attach immediately so status reflects readiness
    // even before the user clicks play.
    attachStatusListeners(el);
  }, [attachStatusListeners, detachListeners, onAudioElement]);

  /**
   * Called synchronously from the play button click handler. On first call
   * builds the entire Web Audio graph in the user-gesture stack so Chrome
   * starts the context in `running` state. Subsequent calls only resume.
   */
  const resumeContext = useCallback(() => {
    const el = elementRef.current;
    if (!el) return;

    const AudioContextCtor =
      window.AudioContext ||
      (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextCtor) return;

    // Lazy build on first user gesture.
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new AudioContextCtor();
      }
      const ctx = audioContextRef.current;

      // `createMediaElementSource` can only be called ONCE per element ever.
      if (tappedElementRef.current !== el) {
        const source = ctx.createMediaElementSource(el);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 2048;
        analyser.smoothingTimeConstant = 0.78;
        source.connect(analyser);
        analyser.connect(ctx.destination);

        mediaSourceRef.current = source;
        analyserRef.current = analyser;
        tappedElementRef.current = el;
        setPlayerAnalyser(analyser);
      }
    } catch (err) {
      // Don't let analyser failures break playback.
      try { console.warn('[useAudioElementAnalyserTap] graph init failed', err); } catch {}
    }

    const ctx = audioContextRef.current;
    if (ctx && ctx.state === 'suspended') {
      void ctx.resume().catch(() => {});
    }
  }, []);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      detachListeners();
      try { mediaSourceRef.current?.disconnect(); } catch {}
      try { analyserRef.current?.disconnect(); } catch {}
      mediaSourceRef.current = null;
      analyserRef.current = null;
      tappedElementRef.current = null;
      const ctx = audioContextRef.current;
      audioContextRef.current = null;
      if (ctx) void ctx.close().catch(() => {});
    };
  }, [detachListeners]);

  return {
    playerAnalyser,
    playerStatus,
    handleAudioElement,
    resumeContext,
  };
}
